import dayjs from 'dayjs'
import { t } from 'i18next'
import Database from 'services/db/Database'
import { RemoveThirdPartyEvents } from 'services/db/Queries/Event'
import { syncLocalCalendarsToServer } from 'utils/api/sync_local_calendars_to_server/sync_local_calendars_to_server'
import { groupToProvider, PersonalGroupSyncStatus } from 'utils/groups'
import { isTrulyEmpty } from 'utils/object'

import { addGroupModelToServer } from '../../providers/CalendarSyncProdivder'
import {
  generateLinkForGroup,
  updateGroupPersonalSyncStatus
} from '../../../services/api/group'
import LocalStorage from '../../../services/LocalStorage'
import { GroupDoc } from '../../../types/api/changes'
import { Model } from '../../../types/model'
import { LocalStorageKey } from '../../../types/services/localStorage'

type GroupProvider = NonNullable<ReturnType<typeof groupToProvider>>

interface SharePersonalCalendarOptions {
  group: Model<GroupDoc>
  syncCalendar: (
    provider: GroupProvider,
    query?: string,
    bigWindow?: boolean
  ) => Promise<void>
  onGroupServer: (groupId: string) => void
  onAfterFirstShareSyncStatusUpdate?: () => Promise<void>
  setFirstShareLocalStatusSynced?: boolean
  removeAddingGroupFlagOnFirstShare?: boolean
  syncExistingGroupAsInitial?: boolean
}

const persistSharedState = async (
  group: Model<GroupDoc>,
  setSyncedStatus: boolean
) => {
  await Database.write(async () => {
    group.update((local) => {
      local._raw['_sortOrder'] = dayjs().unix()

      if (setSyncedStatus) {
        local.PersonalGroupSyncStatus = PersonalGroupSyncStatus.SYNCED
      }

      local.LastUpdateMessage = {
        type: t('youSharedThisCalendar')
      }
    })
  })
}

export const sharePersonalCalendar = async (
  options: SharePersonalCalendarOptions
) => {
  const {
    group,
    syncCalendar,
    onGroupServer,
    onAfterFirstShareSyncStatusUpdate,
    setFirstShareLocalStatusSynced = false,
    removeAddingGroupFlagOnFirstShare = false,
    syncExistingGroupAsInitial = false
  } = options

  if (isTrulyEmpty(group._rev)) {
    await RemoveThirdPartyEvents(group._id)

    const serverGroup = await addGroupModelToServer(group)
    onGroupServer(serverGroup._id ?? '')

    await updateGroupPersonalSyncStatus(PersonalGroupSyncStatus.SYNCED, group)

    if (onAfterFirstShareSyncStatusUpdate) {
      await onAfterFirstShareSyncStatusUpdate()
    }

    const provider = groupToProvider(group)
    if (provider) {
      await syncCalendar(provider, undefined, true)
      await syncLocalCalendarsToServer(true)
    }

    if (removeAddingGroupFlagOnFirstShare) {
      LocalStorage.remove(LocalStorageKey.ADDING_GROUP)
    }

    await persistSharedState(group, setFirstShareLocalStatusSynced)
    return
  }

  await generateLinkForGroup(group)
  await RemoveThirdPartyEvents(group._id)

  const provider = groupToProvider(group)
  if (!provider) {
    return
  }

  if (group.PersonalGroupSyncStatus !== PersonalGroupSyncStatus.SYNCED) {
    await updateGroupPersonalSyncStatus(PersonalGroupSyncStatus.SYNCED, group)
  }

  await persistSharedState(group, true)

  if (syncExistingGroupAsInitial) {
    await syncCalendar(provider, undefined, true)
  } else {
    await syncCalendar(provider)
  }

  await syncLocalCalendarsToServer(true)
}
